import React, { createContext, lazy, useContext, useEffect, useState } from 'react';

import './LoanDetails.scss';
import { formatterCurrency } from '@common/utils/formatters/formatter';
import { BoxValues } from './components/BoxValues/BoxValues';
import { Button } from 'antd';
import { TaskOpenModalContext } from 'scenes/Private/Private';
import warningIcon from 'assets/images/warning-icon.svg';
import checkClosedIcon from 'assets/images/check_closed.svg';
import { useGetTaskResponsesByTask } from 'services/task/querys';
import { TasksContext } from '../../Dashboard';
import { BoxClosing } from './components/BoxClosing/BoxClosing';
import { BoxPartners } from './components/BoxPartners/BoxParteners';
import { BoxLoanDetails } from './components/BoxLoanDetails/BoxLoanDetails';
import { BoxPropertyDetails } from './components/BoxProperty/BoxProperty';
import { BoxBudget } from './components/BoxBudget/BoxBudget';
import { BoxFees } from './components/BoxFees/BoxFees';
import { BoxAdditionalDocuments } from './components/BoxAdditionalDocuments/BoxAdditionalDocuments';
import { BoxLoanDocuments } from './components/BoxLoanDocuments/BoxLoanDocuments';
import { BoxPortfolio } from './components/BoxPortfolio/BoxPortfolio';
import AnnouncementBasic from 'components/Atoms/Announcements/AnnouncementBasic';
import { useGetLoanClosedByLoan } from 'services/loan/querys';

interface ILoanDetailsProps {
  loanSelected: any;
  onClose: () => void;
}

const LoanDetails: React.FC<ILoanDetailsProps> = ({ loanSelected, onClose }) => {
  const tasks: any = useContext(TasksContext);

  const taskAddDocs = tasks.find((item: any) => item.key === 'add-docs');
  const { data: anotherDocs, reexecute: getTaskResponses } = useGetTaskResponsesByTask({
    variables: {
      findResponsesByTask: { taskId: taskAddDocs?._id }
    },
    pause: true
  });
  const { data: loanClosed, reexecute: getLoanClosedByLoan } = useGetLoanClosedByLoan({ variables: { loanId: loanSelected?._id } });
  const { setHandler: setTaskOpenModal }: any = useContext(TaskOpenModalContext);

  useEffect(() => {
    if (tasks.length > 0)
      getTaskResponses({ requestPolicy: 'network-only' });
  }, [tasks])

  useEffect(() => {
    if (loanSelected?._id) {
      getLoanClosedByLoan({ requestPolicy: 'network-only' });
    }
  }, [loanSelected?._id]);

  if (!loanSelected)
    return <>Loading...</>;

  const closedDate = new Date(loanClosed?.[0]?.createdAt).toLocaleDateString('en-US');
  
  const loanOptions = {
    rate: loanSelected?.salesforceData?.selectedOption?.interestRate ? loanSelected?.salesforceData?.selectedOption?.interestRate.toFixed(3) : loanSelected?.loanData?.selectedOption?.rate,
    loanTerm: loanSelected?.salesforceData?.selectedOption?.termInMonths ? `${loanSelected?.salesforceData?.selectedOption?.termInMonths} months` : loanSelected?.loanData?.selectedOption?.loanTerm,
    monthlyPayment: loanSelected?.salesforceData?.selectedOption?.monthlyPayment ? formatterCurrency(loanSelected?.salesforceData?.selectedOption?.monthlyPayment) : formatterCurrency(loanSelected?.loanData?.selectedOption?.monthlyPayment),
    totalLoanAmount: loanSelected?.salesforceData?.selectedOption?.totalLoanAmount ? formatterCurrency(loanSelected?.salesforceData?.selectedOption?.totalLoanAmount) : formatterCurrency(loanSelected?.loanData?.selectedOption?.totalLoanAmount),
    ltv: loanSelected?.salesforceData?.selectedOption?.aivLTV ? loanSelected?.salesforceData?.selectedOption?.aivLTV / 100 : loanSelected?.loanData?.selectedOption?.ltv,
  }

  return (
    <div className='loan-details'>
      <div className='loan-details-top-bar'>
        <div className='loan-info'>
          <span className='loan-address'>LOAN DETAILS</span>
        </div>
        <div className='close-button-container' onClick={onClose}>
          CLOSE X
        </div>
      </div>

      <div className='loan-details-first-row'>
        <div className='loan-progress'>
          <div className='loan-address'>
            {loanSelected.name}
          </div>
        </div>
        {loanSelected.salesforceData?.loanNumber && (
          <span className="loan-number">Loan number: #{loanSelected.salesforceData?.loanNumber}</span>
        )}
      </div>

      {loanSelected?.state !== 'closed' &&
        <div className='loan-details-box-change-terms'>
          <img src={warningIcon} alt="icon" />
          <span>
            These terms are subject to change based on information you submit throughout the loan process. Well reach out if there any changes.
          </span>
        </div>
      }

      {loanSelected?.state === 'closed' && 
        <AnnouncementBasic
          className='closed-loan mb-20'
          icon={checkClosedIcon}
          text={
            <p>This loan was closed on <span>{closedDate}</span>.
              If you have any questions, reach out to your loan officer
              {' '}<a href='mailto:processing@constlending.com'>here</a>
            </p>
           }
        />
      }

      <div className='loan-details-main'>
        <div className='left-column'>
          <div className='loan-summary'>
            <BoxClosing loanSelected={loanSelected} />
            <BoxPartners loanSelected={loanSelected} setTaskOpenModal={setTaskOpenModal} tasks={tasks} />
          </div>
          <BoxValues {...loanOptions} />
          <BoxLoanDetails loanSelected={loanSelected} />
          <BoxPropertyDetails loanSelected={loanSelected} setTaskOpenModal={setTaskOpenModal} tasks={tasks} />
          <BoxBudget loanSelected={loanSelected} />
          <BoxFees loanSelected={loanSelected} />
          {/* <div className='content-box'>
            <div className='label'>
              Notes
            </div>
            <div className='content'>
              <ReactQuill
                value={loanSelected?.salesforceData?.notes || ''}
                readOnly={true}
                theme={'bubble'}
                style={{
                  height: 420,
                  display: loanSelected?.salesforceData?.notes ? 'block' : 'none'
                }}
              />
            </div>
          </div> */}
        </div>
        <div className='right-column'>
          <BoxPortfolio loanSelected={loanSelected} />
          <BoxLoanDocuments loanSelected={loanSelected} tasks={tasks} />
          <BoxAdditionalDocuments loanSelected={loanSelected} anotherDocs={anotherDocs} />
        </div>
      </div>
    </div>
  );
};

export const LoanDetailsModal = (props: any) => {
  const { openSectionRight: visible, closeModal } = props;
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    }
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const getModalWidth = () => {
    // Always full width on mobile
    if (windowWidth <= 768) return '100%';
    
    if (visible) return '60%';

    if (windowWidth >= 1490) return '80%';
    if (windowWidth >= 1315) return '90%';
    return '100%';
  };

  const getButtonWidth = () => {
    // No button on mobile
    if (windowWidth <= 768) return '0%';
    
    if (visible) return '5%';

    if (windowWidth >= 1490) return '20%';
    if (windowWidth >= 1315) return '10%';
    return '0%';
  };

  return (
    <div className="cont-modal">
      {/* {props.renderButtonTrigger(props.handlerSetVisibile)} */}
      {props.visible && (
        <div className="ModalTask">
          <Button
            className="button-close-mask"
            style={{ width: getButtonWidth() }}
            onClick={closeModal}
          />
          <div
            className={`ModalTask-content animate__animated animate__fadeInRight`}
            style={{ width: getModalWidth(), overflow: 'auto' }}
          >
            {props.children}
          </div>
        </div>
      )}
    </div>
  );
}

export default LoanDetails;